import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { apiCallable } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ArrowLeft, Shield, Crown, X, UserMinus, Mail, Copy, Check, Info, Clock } from 'lucide-react'

interface MemberPermissions {
  canManagePersons: boolean
  canManageClassifications: boolean
  feedOnly: boolean
}

interface Member {
  id: string
  email: string
  isAdmin: boolean
  permissions: MemberPermissions
  classificationIds: string[]
  joinedAt?: any
  status?: 'active' | 'pending'
  invitationId?: string
}

interface Classification {
  id: string
  displayName: string
  color: string
}

export function MembersPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useAuth()
  const groupId = searchParams.get('groupId')

  const [members, setMembers] = useState<Member[]>([])
  const [classifications, setClassifications] = useState<Classification[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null)

  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviting, setInviting] = useState(false)
  const [inviteMessage, setInviteMessage] = useState<string | null>(null)
  const [linkCopied, setLinkCopied] = useState(false)
  const [ownerId, setOwnerId] = useState<string | null>(null)

  const loadData = async () => {
    if (!groupId) return

    try {
      setLoading(true)
      setError(null)

      const [membersResult, classificationsResult] = await Promise.all([
        apiCallable('getGroupMembers')({ groupId }),
        apiCallable('getClassifications')({ groupId }),
      ])

      const membersData = membersResult.data as { members: Member[]; ownerId: string | null }
      const classificationsData = classificationsResult.data as { classifications: Classification[] }

      setOwnerId(membersData.ownerId)
      setMembers(membersData.members)
      setClassifications(classificationsData.classifications)
    } catch (err) {
      console.error('Failed to load members:', err)
      setError(t('members.failedToLoad'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [groupId])

  const handleToggleAdmin = async (memberId: string, isAdmin: boolean) => {
    try {
      await apiCallable('updateMemberAdminStatus')({
        groupId,
        memberId,
        isAdmin,
      })
      setMembers((prev) =>
        prev.map((m) => (m.id === memberId ? { ...m, isAdmin } : m))
      )
    } catch (err) {
      console.error('Failed to update admin status:', err)
    }
  }

  const handleTogglePermission = async (
    memberId: string,
    permission: keyof MemberPermissions,
    value: boolean
  ) => {
    const member = members.find((m) => m.id === memberId)
    if (!member) return

    const updatedPermissions = { ...member.permissions, [permission]: value }

    try {
      await apiCallable('updateMemberPermissions')({
        groupId,
        memberId,
        permissions: updatedPermissions,
      })
      setMembers((prev) =>
        prev.map((m) =>
          m.id === memberId ? { ...m, permissions: updatedPermissions } : m
        )
      )
    } catch (err) {
      console.error('Failed to update permissions:', err)
    }
  }

  const handleAddClassification = async (memberId: string, classificationId: string) => {
    const member = members.find((m) => m.id === memberId)
    if (!member || member.classificationIds.includes(classificationId)) return

    const updatedIds = [...member.classificationIds, classificationId]

    try {
      await apiCallable('updateMemberClassifications')({
        groupId,
        memberId,
        classificationIds: updatedIds,
      })
      setMembers((prev) =>
        prev.map((m) =>
          m.id === memberId ? { ...m, classificationIds: updatedIds } : m
        )
      )
    } catch (err) {
      console.error('Failed to update classifications:', err)
    }
  }

  const handleRemoveClassification = async (memberId: string, classificationId: string) => {
    const member = members.find((m) => m.id === memberId)
    if (!member) return

    const updatedIds = member.classificationIds.filter((id) => id !== classificationId)

    try {
      await apiCallable('updateMemberClassifications')({
        groupId,
        memberId,
        classificationIds: updatedIds,
      })
      setMembers((prev) =>
        prev.map((m) =>
          m.id === memberId ? { ...m, classificationIds: updatedIds } : m
        )
      )
    } catch (err) {
      console.error('Failed to update classifications:', err)
    }
  }

  const handleRemoveMember = async () => {
    if (!removingMemberId) return

    try {
      await apiCallable('removeMember')({
        groupId,
        memberId: removingMemberId,
      })
      setMembers((prev) => prev.filter((m) => m.id !== removingMemberId))
    } catch (err) {
      console.error('Failed to remove member:', err)
    } finally {
      setRemovingMemberId(null)
    }
  }

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!inviteEmail.trim() || !groupId) return

    try {
      setInviting(true)
      setInviteMessage(null)
      const result = await apiCallable('inviteMember')({
        groupId,
        email: inviteEmail.trim(),
      })
      const data = result.data as {
        success: boolean
        alreadyMember?: boolean
        alreadyInvited?: boolean
        // Delivery outcome: the invitee can only sign in once their account exists, and
        // only knows to try once the email lands. Report either failure instead of
        // showing a plain "invited" the admin would trust.
        accountReady?: boolean
        emailSent?: boolean
      }
      const email = inviteEmail.trim()

      if (data.alreadyMember) {
        setInviteMessage(t('members.alreadyMember'))
      } else if (data.accountReady === false) {
        setInviteMessage(t('members.inviteAccountFailed', { email }))
      } else if (data.emailSent === false) {
        setInviteMessage(t('members.inviteEmailFailed', { email }))
      } else if (data.alreadyInvited) {
        setInviteMessage(t('members.inviteResent', { email }))
      } else {
        setInviteMessage(t('members.inviteSuccess', { email }))
        setInviteEmail('')
        // Reload member list (includes pending invitations)
        loadData()
      }
    } catch (err) {
      console.error('Failed to invite member:', err)
      setInviteMessage(t('members.failedToLoad'))
    } finally {
      setInviting(false)
    }
  }

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(window.location.origin)
    setLinkCopied(true)
    setTimeout(() => setLinkCopied(false), 2000)
  }

  const isOwner = (member: Member) => member.id === ownerId
  const isSelf = (member: Member) => member.id === user?.uid
  const currentUserIsAdmin = members.find((m) => m.id === user?.uid)?.isAdmin ?? false

  const getAvailableClassifications = (member: Member) =>
    classifications.filter((c) => !member.classificationIds.includes(c.id))

  const getClassificationById = (id: string) =>
    classifications.find((c) => c.id === id)

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (error || !groupId) {
    return (
      <div className="min-h-screen bg-paper-2/40">
        <nav className="bg-[var(--white-frame)] shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16 items-center">
              <Button variant="ghost" onClick={() => navigate('/')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('members.back')}
              </Button>
            </div>
          </div>
        </nav>
        <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <Card>
            <CardContent className="py-12">
              <Alert variant="destructive">
                <AlertDescription>{error || t('members.noGroupSelected')}</AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-paper-2/40">
      {/* Header */}
      <nav className="bg-[var(--white-frame)] shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Button variant="ghost" onClick={() => navigate('/')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('members.back')}
            </Button>
            <h1 className="text-lg font-semibold">{t('members.title')}</h1>
            <div className="w-20" />
          </div>
        </div>
      </nav>

      {/* Members List */}
      <main className="max-w-3xl mx-auto py-6 px-4 sm:px-6 lg:px-8 space-y-4">
        {/* Invite Section */}
        <Card>
          <CardContent className="p-5 space-y-3">
            <p className="text-sm font-medium">{t('members.inviteMember')}</p>
            <form onSubmit={handleInvite} className="flex gap-2">
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder={t('members.emailPlaceholder')}
                disabled={inviting}
                className="flex-1"
              />
              <Button type="submit" disabled={inviting || !inviteEmail.trim()}>
                <Mail className="h-4 w-4 mr-1" />
                {inviting ? t('members.inviting') : t('members.invite')}
              </Button>
            </form>
            {inviteMessage && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>{inviteMessage}</span>
                <Button variant="outline" size="sm" onClick={handleCopyLink}>
                  {linkCopied ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                  {linkCopied ? t('members.linkCopied') : t('members.copyLink')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {members.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              {t('members.noMembers')}
            </CardContent>
          </Card>
        ) : (
          members.map((member) => {
            const owner = isOwner(member)
            const available = getAvailableClassifications(member)

            return (
              <Card key={member.id}>
                <CardContent className="p-5 space-y-4">
                  {/* Email + Badges */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{member.email}</span>
                      {owner && (
                        <Badge variant="default" className="gap-1">
                          <Crown className="h-3 w-3" />
                          {t('members.owner')}
                        </Badge>
                      )}
                      {member.isAdmin && !owner && (
                        <Badge variant="secondary" className="gap-1">
                          <Shield className="h-3 w-3" />
                          {t('members.admin')}
                        </Badge>
                      )}
                      {member.status === 'pending' && (
                        <Badge variant="outline" className="gap-1 text-amber-600 border-amber-300">
                          <Clock className="h-3 w-3" />
                          {t('members.pending')}
                        </Badge>
                      )}
                      {isSelf(member) && (
                        <Badge variant="outline" className="gap-1">
                          {t('members.you')}
                        </Badge>
                      )}
                    </div>

                    {/* Remove Button */}
                    {currentUserIsAdmin && !owner && !isSelf(member) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-[#5b2419] hover:bg-[#f5e3df]"
                        onClick={() => setRemovingMemberId(member.id)}
                      >
                        <UserMinus className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  {/* Admin Toggle (not shown for pending invitations) */}
                  {currentUserIsAdmin && !owner && !isSelf(member) && member.status !== 'pending' && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">
                        {t('members.adminAccess')}
                      </span>
                      <Switch
                        checked={member.isAdmin}
                        onCheckedChange={(checked) =>
                          handleToggleAdmin(member.id, checked)
                        }
                      />
                    </div>
                  )}

                  {/* Permission Toggles (hidden for admins — they have full access) */}
                  {currentUserIsAdmin && !owner && !isSelf(member) && !member.isAdmin && (
                    <div className="space-y-2 border-t pt-3">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        {t('members.permissions')}
                      </p>
                      {(
                        [
                          'canManageClassifications',
                          'canManagePersons',
                          'feedOnly',
                        ] as (keyof MemberPermissions)[]
                      ).map((perm) => (
                        <div
                          key={perm}
                          className="flex items-center justify-between"
                        >
                          <span className="text-sm flex items-center gap-1">
                            {t(`members.permission.${perm}`)}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-3.5 w-3.5 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-[220px]">
                                <p>{t(`members.permissionHint.${perm}`)}</p>
                              </TooltipContent>
                            </Tooltip>
                          </span>
                          <Switch
                            checked={member.permissions[perm]}
                            onCheckedChange={(checked) =>
                              handleTogglePermission(member.id, perm, checked)
                            }
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Classification Access (not shown for owner, admins, or members with canManageClassifications) */}
                  {!owner && !member.isAdmin && !member.permissions.canManageClassifications && <div className="border-t pt-3">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                      {t('members.classificationAccess')}
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      {member.classificationIds.map((cId) => {
                        const cls = getClassificationById(cId)
                        if (!cls) return null
                        return (
                          <Badge
                            key={cId}
                            style={{ backgroundColor: cls.color, color: '#fff' }}
                            className="gap-1 pr-1"
                          >
                            {cls.displayName}
                            {currentUserIsAdmin && !owner && (
                              <button
                                onClick={() =>
                                  handleRemoveClassification(member.id, cId)
                                }
                                className="ml-1 hover:bg-[var(--white-frame)]/20 rounded-full p-0.5"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            )}
                          </Badge>
                        )
                      })}

                      {currentUserIsAdmin && !owner && available.length > 0 && (
                        <Select
                          onValueChange={(value) =>
                            handleAddClassification(member.id, value)
                          }
                        >
                          <SelectTrigger className="w-[160px] h-7 text-xs">
                            <SelectValue
                              placeholder={t('members.addClassification')}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {available.map((cls) => (
                              <SelectItem key={cls.id} value={cls.id}>
                                <div className="flex items-center gap-2">
                                  <div
                                    className="w-3 h-3 rounded-full"
                                    style={{ backgroundColor: cls.color }}
                                  />
                                  {cls.displayName}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}

                      {member.classificationIds.length === 0 && (
                        <span className="text-xs text-muted-foreground">
                          {t('members.noClassifications')}
                        </span>
                      )}
                    </div>
                  </div>}
                </CardContent>
              </Card>
            )
          })
        )}
      </main>

      {/* Remove Member Confirmation */}
      <AlertDialog
        open={!!removingMemberId}
        onOpenChange={(open) => !open && setRemovingMemberId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('members.removeTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('members.removeDescription', {
                email:
                  members.find((m) => m.id === removingMemberId)?.email ?? '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('members.removeConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
